import type { IngredientFormData } from '../types/ingredient';
import type { LucidGreenProductData } from '../types/import';

export interface MappingResult {
  formData: IngredientFormData;
  autoPopulatedFields: Set<keyof IngredientFormData>;
}

/**
 * Maps LucidGreen COA product data to the IngredientFormData shape
 * used by the ingredient form.
 *
 * - String fields are trimmed and truncated to 100 characters
 * - Varietal is matched case-insensitively against Indica/Hybrid/Sativa
 * - Cannabinoid values are clamped to [0, 100] and rounded to 2 decimal places
 * - Non-numeric, NaN, and Infinity cannabinoid values are omitted
 * - The autoPopulatedFields set tracks which fields received COA data
 */
export function mapCoaToIngredientForm(data: LucidGreenProductData): MappingResult {
  const autoPopulatedFields = new Set<keyof IngredientFormData>();
  const formData: IngredientFormData = {
    name: '',
    brand: '',
    strain: '',
    varietal: '',
    thc: '',
    thca: '',
    cbd: '',
    cbda: '',
    cbg: '',
    cbn: '',
  };

  if (data.productName && data.productName.trim().length > 0) {
    formData.name = data.productName.trim().slice(0, 100);
    autoPopulatedFields.add('name');
  }

  if (data.brand && data.brand.trim().length > 0) {
    formData.brand = data.brand.trim().slice(0, 100);
    autoPopulatedFields.add('brand');
  }

  if (data.strain && data.strain.trim().length > 0) {
    formData.strain = data.strain.trim().slice(0, 100);
    autoPopulatedFields.add('strain');
  }

  if (data.varietal) {
    const normalized = data.varietal.trim().toLowerCase();
    if (['indica', 'hybrid', 'sativa'].includes(normalized)) {
      formData.varietal = (normalized.charAt(0).toUpperCase() + normalized.slice(1)) as 'Indica' | 'Hybrid' | 'Sativa';
      autoPopulatedFields.add('varietal');
    }
  }

  const cannabinoidFields = ['thc', 'thca', 'cbd', 'cbda', 'cbg', 'cbn'] as const;
  for (const field of cannabinoidFields) {
    const value = data[field];
    if (value != null && typeof value === 'number' && isFinite(value)) {
      const clamped = Math.max(0, Math.min(100, value));
      const rounded = Math.round(clamped * 100) / 100;
      formData[field] = rounded.toString();
      autoPopulatedFields.add(field);
    }
  }

  return { formData, autoPopulatedFields };
}
